import { useMemo } from 'react';

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useSearchParams } from 'react-router-dom';

import {
  IRoutingConfigPoliciesTableColumns,
  isNotFoundError,
  useGetActiveRoutingConfigVersionQuery,
  useGetRoutingConfigVersionQuery,
  useGetRoutingConfigVersionsQuery,
} from '@entities/routing/config-version';
import ModalOpenBtn from '@features/modal-open-btn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Typography } from '@mui/material';
import ErrorMessage from '@shared/components/ErrorMessage';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import {
  Table,
  TBody,
  THead,
  TableActions,
  TableActionsItem,
  TableContainer,
  TableProvider,
} from '@shared/components/Table';

import ConfigActions from './components/ConfigActions';
import EditorBanner from './components/EditorBanner';
import PolicyEditorForm from './components/PolicyEditorForm';
import VersionMetadata from './components/VersionMetadata';
import VersionSwitcher from './components/VersionSwitcher';
import { IRoutingConfigPolicyRow, useTableSettings } from './settings/useTableSettings';
import { useConfigEditor } from './useConfigEditor';
import './config-view.css';

// The table is not filterable or paginated, so the provider gets inert filter props.
const NOOP = (): void => undefined;
const EMPTY_FILTER = new URLSearchParams();

// One large page for the switcher — the backend has no list filters yet.
const VERSIONS_QUERY = 'size=100';

const RoutingConfigViewWidget = () => {
  const [searchParams] = useSearchParams();
  const versionParam = searchParams.get('version');
  // A missing or malformed ?version falls back to the active config; ids beyond
  // the safe-integer range would silently round to a different version.
  const parsedVersion = versionParam && /^\d+$/.test(versionParam) ? Number(versionParam) : null;
  const versionId = Number.isSafeInteger(parsedVersion) ? parsedVersion : null;

  const versionQuery = useGetRoutingConfigVersionQuery(versionId ?? 0, {
    skip: versionId === null,
  });
  // The widget owns the bar queries too (always subscribed): one spinner, and the
  // data stays warm across switches despite keepUnusedDataFor: 0.
  const activeQuery = useGetActiveRoutingConfigVersionQuery();
  const versionsQuery = useGetRoutingConfigVersionsQuery(VERSIONS_QUERY);

  const { data, isLoading, isFetching, isError, error } =
    versionId === null ? activeQuery : versionQuery;

  const version = data?.data;
  // A 404 means "no active config"; any other failure is "unavailable", not that.
  // Derived before activeVersion: RTK keeps stale data after a failed refetch,
  // which save/publish invalidations make reachable.
  const isActiveUnavailable = activeQuery.isError && !isNotFoundError(activeQuery.error);
  const activeVersion = isActiveUnavailable ? null : activeQuery.data?.data ?? null;
  const isDiverged = activeVersion !== null && version != null && version.id !== activeVersion.id;

  const versions = useMemo(() => versionsQuery.data?.data ?? [], [versionsQuery.data]);
  const editor = useConfigEditor({ version, versionId, versions });

  const rows = useMemo<IRoutingConfigPolicyRow[]>(
    () => editor.policies.map(policy => ({ ...policy, id: policy.uid })),
    [editor.policies],
  );
  const policyUids = useMemo(() => rows.map(row => row.uid), [rows]);

  const { columnTitles, renderItem, renderRow } = useTableSettings({
    isEditing: editor.isEditing,
    changedUids: editor.changes.changedUids,
    addedUids: editor.changes.addedUids,
    changedRuleUids: editor.changes.changedRuleUids,
    addedRuleUids: editor.changes.addedRuleUids,
    policyUids,
    onTogglePolicy: editor.onTogglePolicy,
    onUpdatePolicy: editor.onUpdatePolicy,
    onClonePolicy: editor.onClonePolicy,
    onRemovePolicy: editor.onRemovePolicy,
    onMovePolicy: editor.onMovePolicy,
    onMoveRule: editor.onMoveRule,
    onAddRule: editor.onAddRule,
    onUpdateRule: editor.onUpdateRule,
    onRemoveRule: editor.onRemoveRule,
    onToggleRule: editor.onToggleRule,
  });

  if (isLoading || isFetching || activeQuery.isLoading || versionsQuery.isLoading)
    return <LoadingSpinner />;

  if (isError) {
    if (isNotFoundError(error)) {
      return (
        <Typography variant='body1' className='routing-config-empty'>
          {versionId === null
            ? 'There is no active routing config yet.'
            : `Routing config version v${versionId} was not found.`}
        </Typography>
      );
    }
    return <ErrorMessage error={error} />;
  }

  if (!version) return null;

  return (
    <div className='routing-config-view'>
      <VersionSwitcher
        viewingVersion={version}
        versions={versions}
        isVersionsError={versionsQuery.isError}
        activeVersion={activeVersion}
        isActiveUnavailable={isActiveUnavailable}
        isDirty={editor.isEditing && editor.changes.isDirty}
        // A Restore in flight locks the picker too: its draft is about to take the URL.
        isSaving={editor.isSaving || editor.isRestoring}
        onDiscardConfirmed={editor.onDiscard}
        actions={
          <ConfigActions
            version={version}
            isEditing={editor.isEditing}
            isRestoring={editor.isRestoring}
            onEdit={editor.onEdit}
            onRestore={editor.onRestore}
          />
        }
      />
      {editor.isEditing && editor.session && (
        <EditorBanner
          versionId={version.id}
          activeVersionId={activeVersion?.id ?? null}
          changes={editor.changes}
          serverIssues={editor.serverIssues}
          policies={editor.session.draft}
          isSaving={editor.isSaving}
          onDiscard={editor.onDiscard}
          onSave={editor.onSave}
        />
      )}
      <VersionMetadata
        version={version}
        isDiverged={isDiverged}
        onEditDetails={editor.isEditing || editor.isRestoring ? undefined : editor.onEditDetails}
      />

      <p className='routing-config-info-alert'>
        <InfoOutlinedIcon fontSize='small' />
        Policies are processed in order of priority. Policies at the top have higher priority.
      </p>

      <DndProvider backend={HTML5Backend}>
        {/* Re-keyed: the provider seeds column visibility once, and edit mode adds a column */}
        <TableProvider<IRoutingConfigPoliciesTableColumns>
          key={editor.isEditing ? 'editing' : 'viewing'}
          columnTitles={columnTitles}
          currentFilter={EMPTY_FILTER}
          updateFilter={NOOP}
          resetFilter={NOOP}
          deleteFilterKey={NOOP}
          page='1'
          setPage={NOOP}
          size='50'
          setSize={NOOP}
        >
          {editor.isEditing && (
            <TableActions>
              <TableActionsItem>
                <ModalOpenBtn
                  text='New policy'
                  size='md'
                  modalContent={<PolicyEditorForm onSubmit={editor.onAddPolicy} />}
                />
              </TableActionsItem>
            </TableActions>
          )}

          {rows.length ? (
            <TableContainer id='routing-config-policies-table'>
              <Table>
                <THead<IRoutingConfigPoliciesTableColumns>
                  renderItem={key => {
                    // Invisible :has() hooks for the CSS column alignment.
                    if (key === 'status') return <span className='routing-onoff-col-marker' />;
                    if (key === 'actions') return <span className='routing-actions-col-marker' />;
                    return null;
                  }}
                />
                <TBody items={rows} renderItem={renderItem} renderRow={renderRow} />
              </Table>
            </TableContainer>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              This version has no policies.
            </Typography>
          )}
        </TableProvider>
      </DndProvider>
    </div>
  );
};

export default RoutingConfigViewWidget;
